import { ReactNode, useCallback } from 'react';
import { components } from '@api/play-account/generated';

import { cx } from '@libs/classnames';
import { formatDate } from '@libs/dates';
import { DateView } from '@ui/components/DateView';
import { Fraction } from '@ui/components/Fraction';
import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { Table, TableCell, TableRow } from '@ui/components/Table';
import { DownloadIcon } from '@ui/icons/DownloadIcon/DownloadIcon';

import styles from './PlayerGamesList.module.css';

type Props = {
  className?: string;
  games: components['schemas']['UserGameHistoryItemDto'][];
  onDownloadPgn?: (gameId: string) => void;
  onGameClick: (game: components['schemas']['UserGameHistoryItemDto']) => void;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};
export function PlayerGamesList({
  className,
  games,
  onDownloadPgn,
  onGameClick,
  renderShieldQuickPeek,
}: Props) {
  let prevDay: string;

  return (
    <HorizontalScroll className={cx(styles.wrapper, className)}>
      <Table className={styles.gamesList}>
        {games.map((game) => {
          const date = new Date(game.gameStartTimeStamp!);
          const day = formatDate(date, { timezone: 'utc' });
          const showDate = day !== prevDay;
          prevDay = day;
          return (
            <PlayerGameItem
              key={game.id}
              game={game}
              showDate={showDate}
              onDownloadPgn={onDownloadPgn}
              onGameClick={onGameClick}
              renderShieldQuickPeek={renderShieldQuickPeek}
            />
          );
        })}
      </Table>
    </HorizontalScroll>
  );
}

type PlayerGameItem = {
  game: components['schemas']['UserGameHistoryItemDto'];
  showDate: boolean;
  onDownloadPgn?: (gameId: string) => void;
  onGameClick: (game: components['schemas']['UserGameHistoryItemDto']) => void;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};
function PlayerGameItem({
  game,
  showDate,
  onDownloadPgn,
  onGameClick,
  renderShieldQuickPeek,
}: PlayerGameItem) {
  const onNameClick = useCallback(() => onGameClick(game), [onGameClick, game]);

  const isWin = game.result === 'Win';
  const isLoose = game.result === 'Loss';
  const isTie = game.result === 'Draw';

  return (
    <TableRow>
      <TableCell className={styles.date}>
        {showDate && (
          <DateView
            date={game.gameStartTimeStamp}
            humanReadableDay={false}
            shortMonth
            year="hide"
            timezone="utc"
          />
        )}
      </TableCell>
      <TableCell className={styles.nameColumn}>
        <div className={styles.nameContainer}>
          <div className={styles.name} onClick={onNameClick}>
            {game.opponentFirstName?.slice(0, 1)}.&nbsp;{game.opponentLastName}
            &nbsp;
          </div>
          {renderShieldQuickPeek && renderShieldQuickPeek(game.opponentUserId || '')}
        </div>
      </TableCell>
      <TableCell
        className={cx(styles.result, {
          [styles.win]: isWin,
          [styles.loose]: isLoose,
        })}
        right
      >
        <Fraction value={isWin ? '1' : isTie ? '1/2' : '0'} />
      </TableCell>
      <TableCell className={cx(styles.delta, { [styles.loose]: isLoose })} right>
        {game.eloChange > 0 ? `+${game.eloChange}` : game.eloChange}
      </TableCell>
      {onDownloadPgn && (
        <TableCell className={styles.downloadButtonColumn} right>
          <div className={styles.downloadButton} onClick={() => onDownloadPgn(game.gameId)}>
            <DownloadIcon />
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}
